import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO55000_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 55000 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Asset Management System (AMS) — International standard for
            optimizing physical assets, maximizing value, and minimizing risks
            across the lifecycle.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 55000 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 55000 is the international standard for Asset Management
              Systems (AMS), providing a framework for organizations to
              effectively manage their physical assets to maximize their value,
              performance, and lifecycle. ISO 55000 helps businesses adopt a
              structured approach to asset management, ensuring that they get
              the most out of their assets while minimizing risks, reducing
              costs, and improving operational efficiency.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO 55000 certification demonstrates a company’s
              commitment to best practices in asset management, driving value
              through optimized asset performance and effective risk management.
              Whether managing infrastructure, machinery, or buildings, ISO
              55000 ensures that organizations can achieve strategic objectives
              while sustaining long-term asset health and performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 55000 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 55000 certification is an international standard that sets out
              the requirements for an Asset Management System (AMS). The
              certification helps organizations align their asset management
              practices with their business strategy, ensuring that assets are
              used efficiently and effectively throughout their lifecycle—from
              acquisition to disposal.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO 55000 focuses on:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Optimizing asset performance: Maximizing the value derived from assets, reducing downtime, and ensuring that assets are performing as intended.",
                "Managing asset risks: Identifying and mitigating risks associated with asset failure, financial loss, or service disruption.",
                "Aligning asset management with business goals: Ensuring that asset management decisions are made in line with organizational objectives and strategy.",
                "Ensuring sustainability and compliance: Ensuring that asset management practices support sustainability goals and meet legal, environmental, and regulatory requirements.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 55000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Asset Management System (AMS) Framework",
                desc: "ISO 55000 provides a structured framework for implementing an Asset Management System (AMS) that helps organizations manage physical assets, align asset management practices with business strategy, and drive performance improvements.",
              },
              {
                icon: Target,
                title: "Risk-Based Approach",
                desc: "The standard promotes a risk-based approach to asset management, ensuring that organizations identify and address potential risks that could disrupt asset performance or lead to financial loss. It helps prioritize risks and allocate resources accordingly to mitigate them.",
              },
              {
                icon: TrendingUp,
                title: "Lifecycle Asset Management",
                desc: "ISO 55000 emphasizes the importance of managing assets across their entire lifecycle, from planning and acquisition to operation, maintenance, and disposal. This ensures that assets are utilized to their full potential and that organizations maximize return on investment (ROI).",
              },
              {
                icon: Users,
                title: "Stakeholder Engagement and Alignment",
                desc: "The standard encourages collaboration among stakeholders to align asset management goals with broader organizational objectives. It also ensures that asset management activities support the needs of employees, customers, and other stakeholders.",
              },
              {
                icon: BarChart3,
                title: "Continuous Improvement",
                desc: "ISO 55000 supports continuous improvement by encouraging regular monitoring, review, and optimization of asset management practices. Organizations are required to evaluate performance, implement improvements, and adapt to changes in business requirements or external conditions.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 55000 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Optimized Asset Performance — ISO 55000 helps businesses optimize the performance of their assets, ensuring that they operate at peak efficiency, reduce downtime, and minimize the total cost of ownership over the asset lifecycle.",
                "Enhanced Risk Management — By implementing the principles of ISO 55000, organizations can identify and manage risks associated with asset failure, aging infrastructure, or service disruptions, helping to prevent costly issues and ensure business continuity.",
                "Cost Reduction and Efficiency — ISO 55000 helps organizations reduce operational costs by maximizing the value derived from assets, improving maintenance practices, and optimizing asset utilization. It also helps reduce unplanned maintenance and repairs, resulting in cost savings.",
                "Better Decision-Making — The certification enables businesses to make more informed decisions regarding asset management by providing data-driven insights into asset performance, risk levels, and lifecycle costs. This leads to better strategic planning and resource allocation.",
                "Regulatory Compliance — Achieving ISO 55000 certification helps organizations meet legal and regulatory requirements related to asset management. This ensures that the organization complies with industry standards and regulations, reducing the risk of non-compliance and penalties.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 55000 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 55000 outlines the key principles and requirements for an
              Asset Management System (AMS). The main components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Leadership Commitment — Senior management must demonstrate leadership and commitment to asset management by ensuring that an Asset Management Policy is established, resources are allocated, and asset management objectives are aligned with organizational goals.",
                "2. Asset Management Strategy and Objectives — Develop a clear asset management strategy that defines the organization’s approach to managing its assets. This should align with business objectives and be guided by performance targets, risk management, and sustainability goals.",
                "3. Risk Management and Asset Lifecycle Management — Implement a risk management framework that identifies, assesses, and mitigates risks associated with asset management. Focus on managing assets throughout their entire lifecycle to ensure value is delivered over time.",
                "4. Asset Performance Monitoring — Establish processes to monitor and evaluate asset performance regularly. Use key performance indicators (KPIs) to track efficiency, effectiveness, and value delivered by assets, ensuring that assets are aligned with organizational goals.",
                "5. Continuous Improvement — ISO 55000 promotes continuous improvement of the asset management system by ensuring that assets are managed efficiently, risks are mitigated, and performance is continuously optimized based on feedback and data analysis.",
                "6. Stakeholder Engagement — Engage key stakeholders, including employees, customers, suppliers, and regulatory bodies, in the asset management process to ensure that their needs are met and that asset management objectives align with broader organizational goals.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 55000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Asset Management Efficiency",
                "Risk Reduction and Mitigation",
                "Cost Savings and Value Optimization",
                "Enhanced Regulatory Compliance",
                "Better Decision Making and Strategic Planning",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 55000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO 55000 certification typically involves the following
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop an Asset Management Strategy — Define the scope and objectives of your Asset Management System (AMS), ensuring alignment with your organization’s business strategy and stakeholder needs.",
                "2. Conduct a Risk Assessment — Identify and assess risks associated with asset performance, including financial, operational, and environmental risks. Develop strategies to mitigate these risks.",
                "3. Implement Asset Management Practices — Develop and implement asset management policies and procedures that support the effective management of assets across their entire lifecycle, from acquisition to disposal.",
                "4. Monitor and Measure Performance — Establish KPIs and performance metrics to track asset performance and ensure that assets are delivering value. Continuously monitor and review asset management practices to identify areas for improvement.",
                "5. Internal Audits and Documentation — Conduct regular internal audits to assess the effectiveness of your Asset Management System and ensure compliance with ISO 55000 standards. Maintain proper documentation to support audits and certification.",
                "6. Third-Party Certification Audit — Engage an accredited certification body to conduct a third-party audit to assess the effectiveness of your AMS and verify compliance with the ISO 55000 standard.",
                "7. Continuous Improvement — Implement a continuous improvement process to optimize asset management practices, ensuring that assets are effectively managed, performance is enhanced, and risks are mitigated over time.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 55000 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert ISO 55000 certification
              consultancy services, helping businesses develop and implement
              asset management systems that optimize asset performance, ensure
              risk management, and align with business objectives.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Asset Management Strategy Development",
                "Risk Assessment and Mitigation",
                "Asset Lifecycle Management",
                "Performance Monitoring and Reporting",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Improvement and Optimization",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 55000 certification, ensuring
              optimized asset management, value maximization, and strategic
              alignment with organizational goals.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO55000_Details;
